import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

const EDGE_FN_URL = 'https://ttvxezxhcdxwsajhcjtl.supabase.co/functions/v1/send-notification';

type TestResult = {
  success: boolean;
  message: string;
  detail?: string;
  resendId?: string;
  ts: string;
};

const TEST_SCENARIOS = [
  { label: 'Shipment Created', status: 'pending', icon: 'ri-add-circle-line', color: '#f59e0b', description: 'Mimics the email sent when a new shipment is registered' },
  { label: 'Picked Up', status: 'picked_up', icon: 'ri-hand-coin-line', color: '#a78bfa', description: 'Package has been collected from sender' },
  { label: 'In Transit', status: 'in_transit', icon: 'ri-truck-line', color: '#38bdf8', description: 'Package is en route to destination' },
  { label: 'Out for Delivery', status: 'out_for_delivery', icon: 'ri-e-bike-line', color: '#fb923c', description: 'Package is out for final delivery today' },
  { label: 'Delivered', status: 'delivered', icon: 'ri-checkbox-circle-line', color: '#4ade80', description: 'Package has been successfully delivered' },
  { label: 'On Hold', status: 'hold', icon: 'ri-pause-circle-line', color: '#f87171', description: 'Package held due to customs or other reason' },
];

export default function AdminEmailTestTab() {
  const [toEmail, setToEmail] = useState('');
  const [selectedScenario, setSelectedScenario] = useState(TEST_SCENARIOS[0]);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);

  const handleSendTest = async () => {
    if (!toEmail.trim() || sending) return;
    setSending(true);
    const ts = new Date().toLocaleTimeString();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const testTrackingId = `SWT-TEST-${Date.now().toString().slice(-6)}`;

      const res = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          trackingId: testTrackingId,
          receiverEmail: toEmail.trim(),
          receiverName: 'Test Recipient',
          newStatus: selectedScenario.status,
          statusLabel: selectedScenario.label,
          currentLocation: 'London Hub, United Kingdom',
          reason: `This is a test email sent from the SwiftTrack admin panel to verify email delivery is working correctly. Scenario: ${selectedScenario.label}.`,
          estimatedDelivery: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          origin: 'London, United Kingdom',
          destination: 'New York, United States',
          siteUrl: window.location.origin,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setResults((prev) => [{
          success: true,
          message: `Email delivered to ${toEmail.trim()}`,
          detail: `Tracking ID: ${testTrackingId} · Resend message ID: ${data.id}`,
          resendId: data.id,
          ts,
        }, ...prev]);
      } else {
        const errMsg = typeof data.error === 'object'
          ? JSON.stringify(data.error)
          : String(data.error || 'Unknown error');
        setResults((prev) => [{
          success: false,
          message: `Delivery failed (HTTP ${res.status})`,
          detail: errMsg,
          ts,
        }, ...prev]);
      }
    } catch (err) {
      setResults((prev) => [{
        success: false,
        message: 'Request failed',
        detail: err instanceof Error ? err.message : String(err),
        ts,
      }, ...prev]);
    } finally {
      setSending(false);
    }
  };

  const inputCls = 'w-full bg-[#1a3333] border border-teal-900/40 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-700 focus:border-teal-600 transition-colors';

  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* Header info card */}
      <div className="bg-amber-950/30 border border-amber-700/40 rounded-2xl px-5 py-4 flex items-start gap-4">
        <div className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
          <i className="ri-mail-settings-line text-amber-400 text-lg" />
        </div>
        <div>
          <div className="text-amber-300 font-semibold text-sm mb-1">Email Delivery Diagnostics</div>
          <div className="text-amber-700 text-xs leading-relaxed">
            Sends a real test email through the Resend API using <strong className="text-amber-500">support@swift-tracks.com</strong> as the sender.
            Use this to verify your domain is working and emails land in the inbox (not spam).
          </div>
        </div>
      </div>

      {/* Test form */}
      <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6 space-y-5">
        <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider flex items-center gap-2">
          <i className="ri-send-plane-line text-teal-400" />
          Send Test Email
        </h3>

        {/* Recipient */}
        <div>
          <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">
            Recipient Email Address
          </label>
          <div className="relative">
            <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-teal-600 text-sm pointer-events-none" />
            <input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendTest(); }}
              placeholder="you@example.com"
              className={`${inputCls} pl-10`}
            />
          </div>
          <p className="text-teal-800 text-xs mt-1.5">The test email will be delivered to this address</p>
        </div>

        {/* Scenario selector */}
        <div>
          <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-3">
            Email Template / Scenario
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {TEST_SCENARIOS.map((s) => {
              const isActive = selectedScenario.status === s.status;
              return (
                <button
                  key={s.status}
                  type="button"
                  onClick={() => setSelectedScenario(s)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all cursor-pointer"
                  style={{
                    borderColor: isActive ? s.color : 'rgba(13,148,136,0.2)',
                    background: isActive ? `${s.color}18` : '#1a3333',
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: isActive ? `${s.color}25` : 'rgba(255,255,255,0.04)' }}
                  >
                    <i
                      className={`${s.icon} text-base`}
                      style={{ color: isActive ? s.color : '#4d9e9e' }}
                    />
                  </div>
                  <div className="min-w-0">
                    <div
                      className="text-xs font-semibold leading-tight truncate"
                      style={{ color: isActive ? s.color : '#5eead4' }}
                    >
                      {s.label}
                    </div>
                  </div>
                  {isActive && (
                    <i className="ri-check-line ml-auto flex-shrink-0 text-xs" style={{ color: s.color }} />
                  )}
                </button>
              );
            })}
          </div>
          {selectedScenario && (
            <p className="text-teal-700 text-xs mt-2 flex items-center gap-1.5">
              <i className="ri-information-line" />
              {selectedScenario.description}
            </p>
          )}
        </div>

        {/* What will be sent */}
        <div className="bg-[#0b1e1e] border border-teal-800/30 rounded-xl p-4 space-y-2">
          <p className="text-teal-600 text-xs font-semibold uppercase tracking-wide mb-3">What will be sent</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-teal-700">From:</span>
              <span className="text-teal-300 font-medium">support@swift-tracks.com</span>
            </div>
            <div className="flex justify-between">
              <span className="text-teal-700">To:</span>
              <span className="text-teal-300 font-medium truncate max-w-[140px]">{toEmail || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-teal-700">Template:</span>
              <span className="text-teal-300 font-medium">{selectedScenario.label}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-teal-700">Tracking ID:</span>
              <span className="text-teal-300 font-medium font-mono">SWT-TEST-XXXXXX</span>
            </div>
            <div className="flex justify-between">
              <span className="text-teal-700">Route:</span>
              <span className="text-teal-300 font-medium">London → New York</span>
            </div>
            <div className="flex justify-between">
              <span className="text-teal-700">API:</span>
              <span className="text-teal-300 font-medium">Resend.com</span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSendTest}
          disabled={!toEmail.trim() || sending}
          className="w-full py-4 rounded-2xl text-sm font-semibold text-white transition-opacity hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          style={{ background: 'var(--primary)' }}
        >
          {sending ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Sending test email...
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill" />
              Send Test Email Now
            </>
          )}
        </button>
      </div>

      {/* Results log */}
      {results.length > 0 && (
        <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider flex items-center gap-2">
              <i className="ri-history-line text-teal-400" />
              Test Results
            </h3>
            <button
              onClick={() => setResults([])}
              className="text-teal-700 hover:text-teal-400 text-xs cursor-pointer flex items-center gap-1"
            >
              <i className="ri-delete-bin-6-line" />
              Clear
            </button>
          </div>

          <div className="space-y-3">
            {results.map((r, i) => (
              <div
                key={i}
                className="rounded-xl border px-4 py-4"
                style={{
                  background: r.success ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.06)',
                  borderColor: r.success ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)',
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: r.success ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)' }}
                  >
                    <i
                      className={r.success ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'}
                      style={{ color: r.success ? '#10b981' : '#ef4444' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-semibold text-sm"
                      style={{ color: r.success ? '#10b981' : '#ef4444' }}
                    >
                      {r.success ? 'SUCCESS' : 'FAILED'} — {r.message}
                    </div>
                    {r.detail && (
                      <div className="text-xs text-teal-600 mt-0.5 break-all">{r.detail}</div>
                    )}
                  </div>
                  <span className="text-teal-700 text-xs flex-shrink-0">{r.ts}</span>
                </div>

                {r.success && (
                  <div className="mt-3 bg-[#0b1e1e] border border-teal-800/30 rounded-lg px-3 py-2 flex items-start gap-2">
                    <i className="ri-information-line text-teal-500 text-sm flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-teal-500 leading-relaxed">
                      Email was accepted by Resend. Check your inbox (and spam folder). If it doesn&apos;t arrive within 2 minutes, check your Resend dashboard for delivery logs.
                    </div>
                  </div>
                )}

                {!r.success && r.detail?.includes('domain') && (
                  <div className="mt-3 bg-red-950/20 border border-red-900/30 rounded-lg px-3 py-2 flex items-start gap-2">
                    <i className="ri-alert-line text-red-400 text-sm flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-red-400 leading-relaxed">
                      Domain verification issue. Go to your Resend dashboard → Domains and make sure <strong>swift-tracks.com</strong> is verified with DNS records.
                    </div>
                  </div>
                )}

                {!r.success && r.detail?.includes('RESEND_API_KEY') && (
                  <div className="mt-3 bg-red-950/20 border border-red-900/30 rounded-lg px-3 py-2 flex items-start gap-2">
                    <i className="ri-key-line text-red-400 text-sm flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-red-400 leading-relaxed">
                      <strong>RESEND_API_KEY is not configured.</strong> Go to Supabase → Edge Functions → Secrets and add your Resend API key as <code className="bg-red-900/30 px-1 py-0.5 rounded">RESEND_API_KEY</code>.
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help section */}
      <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6">
        <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider flex items-center gap-2 mb-4">
          <i className="ri-question-line text-teal-400" />
          Troubleshooting Email Delivery
        </h3>
        <div className="space-y-4">
          {[
            {
              icon: 'ri-key-line',
              color: '#f59e0b',
              title: 'RESEND_API_KEY not set',
              desc: 'Go to Supabase → Edge Functions → Secrets → Add a new secret named RESEND_API_KEY with your Resend API key value.',
            },
            {
              icon: 'ri-global-line',
              color: '#38bdf8',
              title: 'Domain not verified in Resend',
              desc: 'Go to resend.com → Domains → swift-tracks.com and verify all DNS records (SPF, DKIM, DMARC) are added and verified.',
            },
            {
              icon: 'ri-spam-2-line',
              color: '#f87171',
              title: 'Email going to spam',
              desc: 'Add the Resend SPF and DKIM DNS records to your domain. This tells email providers the mail is legitimate.',
            },
            {
              icon: 'ri-mail-forbid-line',
              color: '#a78bfa',
              title: 'Email created but no notification',
              desc: 'Receiver email field must be filled in when creating a shipment. No email = no notification. This was also a bug — now fixed.',
            },
          ].map((tip) => (
            <div key={tip.title} className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: `${tip.color}18`, border: `1px solid ${tip.color}30` }}
              >
                <i className={`${tip.icon} text-sm`} style={{ color: tip.color }} />
              </div>
              <div>
                <div className="text-white text-xs font-semibold">{tip.title}</div>
                <div className="text-teal-600 text-xs mt-0.5 leading-relaxed">{tip.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
